import 'zone.js'; // Included with Angular CLI.

import { initFederation } from '@angular-architects/native-federation';
import { buildManifestHost, buildManifestOverrides, getMfeManifest, getRegion, loadRuntimeEnv } from './util/federation-overrides.util';
(window as any).__webpack_public_path__ = window.location.origin + '/';

type RemoteManifest = Record<string, string>;

type ManifestWindow = Window & {
  __mfeManifest?: RemoteManifest;
};

function getRouteSegment(pathname: string): string {
  return pathname.replace(/^\/+/, '').split('/')[0] || '';
}

function getStartupRemotes(fullManifest: RemoteManifest, pathname: string): RemoteManifest {
  const segment = getRouteSegment(pathname);

  const segmentToRemote: Record<string, string> = {
    onboarding: 'gpp-account-mfe',
    home: 'gpp-orders-mfe',
    resources: 'gpp-core-mfe',
    billing: 'gpp-ordering-mfe',
    documents: 'gpp-ordering-mfe',
    'open-items': 'gpp-open-items-mfe',
    ordering: 'gpp-ordering-mfe',
    lbgard: 'gpp-ordering-mfe',
    orders: 'gpp-orders-mfe',
    patients: 'gpp-orders-mfe',
    patient: 'gpp-ordering-mfe',
    provider: 'gpp-ordering-mfe',
    'specimen-retrieval': 'gpp-ordering-mfe',
    cancerguard: 'gpp-ordering-mfe',
    hct: 'gpp-ordering-mfe',
    mrd: 'gpp-ordering-mfe',
    odx: 'gpp-ordering-mfe',
    oncoextra: 'gpp-ordering-mfe',
    ccadmin: 'gpp-admin-mfe',
    'sample-processing': 'gpp-orders-mfe',
    login: 'gpp-account-mfe',
    callback: 'gpp-account-mfe',
    logout: 'gpp-account-mfe',
    account: 'gpp-account-mfe',
    contact: 'gpp-account-mfe'
  };

  const startupRemoteName = segmentToRemote[segment] ?? 'gpp-account-mfe';
  const startupManifest: RemoteManifest = {};

  if (fullManifest['gpp-core-mfe']) {
    startupManifest['gpp-core-mfe'] = fullManifest['gpp-core-mfe'];
  }
  if (fullManifest[startupRemoteName]) {
    startupManifest[startupRemoteName] = fullManifest[startupRemoteName];
  }

  return startupManifest;
}

async function main() {
  const env = await loadRuntimeEnv();

  const host = buildManifestHost(env); // e.g., https://provider-portal-*.exactsciences.com/federation.manifest.json
  const region = getRegion(env); // optional (e.g., "eu")

  const [base, overrides] = await Promise.all([getMfeManifest(host, region), Promise.resolve(buildManifestOverrides(env))]);
  // Verify setup by logging the resolved values (optional)
  // console.log(host, region, base, overrides);

  // IMPORTANT: initFederation expects key->URL map only or the url of a json
  // we build the manifest by merging the base manifest fetched from
  // the provider with any overrides we have locally (e.g., for local development or testing)
  const manifest: Record<string, string> = Object.assign(base, overrides);
  (window as ManifestWindow).__mfeManifest = manifest;
  return getStartupRemotes(manifest, window.location.pathname);
}

main()
  .then(manifest => initFederation(manifest))
  .catch(err => console.error(err))
  .then(_ => import('./bootstrap'))
  .catch(err => console.error(err));

// Initialise the Helix localization-provider singleton so components that use
// lit-localize (date-picker, calendar) can render. Without this, `getLocale`
// on the singleton is undefined and the date-picker render throws. Source
// locale (`en`) bypasses the loader, so passing an empty map is safe — wire
// up real locale templates here if/when this app needs runtime translations.
import('@helix-design-system/components/dist/components/localization-provider/localization-provider.js')
  .then(({ initConfig }) => initConfig(new Map()))
  .catch(err => console.error('Failed to initialise Helix localization-provider', err));

Promise.allSettled([
  import('@helix-design-system/components/dist/components/accordion/index.js'),
  import('@helix-design-system/components/dist/components/alert/index.js'),
  import('@helix-design-system/components/dist/components/button/index.js'),
  import('@helix-design-system/components/dist/components/checkbox/index.js'),
  import('@helix-design-system/components/dist/components/drawer/index.js'),
  import('@helix-design-system/components/dist/components/empty-state/index.js'),
  import('@helix-design-system/components/dist/components/icon/index.js'),
  import('@helix-design-system/components/dist/components/pagination/index.js'),
  import('@helix-design-system/components/dist/components/progress-ring/index.js'),
  import('@helix-design-system/components/dist/components/select/index.js'),
  import('@helix-design-system/components/dist/components/tab-group/index.js'),
  import('@helix-design-system/components/dist/components/tag/index.js'),
  import('@helix-design-system/components/dist/components/text-field/index.js'),
  import('@helix-design-system/components/dist/components/textarea/index.js'),
  import('@helix-design-system/components/dist/components/toggle/index.js'),
  import('@helix-design-system/components/dist/components/toggletip/index.js'),
  import('@helix-design-system/components/dist/components/skeleton/index.js'),
  import('@helix-design-system/components/dist/components/tooltip/index.js'),
  import('@helix-design-system/components/dist/components/divider/index.js'),
  import('@helix-design-system/components/dist/components/link/index.js'),
  import('@helix-design-system/components/dist/components/password/index.js'),
  import('@helix-design-system/components/dist/components/radio-group/index.js'),
  import('@helix-design-system/components/dist/components/card/index.js'),
  import('@helix-design-system/components/dist/components/date-picker/index.js'),
  import('@helix-design-system/components/dist/components/phone/index.js'),
  import('@helix-design-system/components/dist/components/badge/index.js'),
  import('@helix-design-system/components/dist/components/menu/index.js'),
  import('@helix-design-system/components/dist/components/modal/index.js')
]).then(results => {
  for (const result of results) {
    if (result.status === 'rejected') {
      console.error('Failed to load Helix components ', result.reason);
    }
  }
});
